import React, { FunctionComponent, memo, useEffect, useMemo, useState } from 'react'
import QRCode from 'react-qr-code'
import Modal from 'blocks.simple/modal/modal'
import Icon from 'blocks.simple/icon/icon'
import { Button } from 'molecules/Button'
import { Checkbox } from 'molecules/Checkbox'
import { OtpCodeInput } from 'molecules/OtpCodeInput'
import translate from 'core/translate'
import { cn } from 'ethcss'
import {
    BeginSecondFactorActionResponse,
    ConfirmSecondFactorActionResult,
    isBackupCodeComplete,
    isOtpComplete,
    isProofCodeReady,
} from 'core/models/SecondFactor'
import type { StartSecondFactorFlowParams } from 'core/hooks/useSecondFactorFlow'
import { secondFactorActionStyles as styles } from './SecondFactorAction-styles'
import { SecondFactorActionStepper } from './SecondFactorActionStepper-view'
import type { SecondFactorStepperIndex } from './SecondFactorActionStepper-view'

export interface SecondFactorActionModalProps {
    open: boolean
    step: 'proof' | 'totp_setup' | 'recovery_codes' | 'done'
    isBusy: boolean
    errorKey?: string
    begin?: BeginSecondFactorActionResponse | null
    setup?: ConfirmSecondFactorActionResult | null
    recoveryCodes: string[]
    proofCode: string
    totpCode: string
    savedAck: boolean
    maskedEmail?: string
    isEmailConfirmed?: boolean
    pendingAction?: StartSecondFactorFlowParams | null
    onChangeProofCode: (value: string) => void
    onChangeTotpCode: (value: string) => void
    onChangeSavedAck: (value: boolean) => void
    onSubmitProof: (codeOverride?: string) => void
    onResendProof?: () => void
    onSubmitTotpSetup: (codeOverride?: string) => void
    onFinalizeRecoveryCodes: () => void
    onCompleteDone: () => void
    onClose: () => void
}

const TOTP_APPS = ['Яндекс ID', 'FreeOTP', 'Google Authenticator']

const formatSecret = (secret?: string) => {
    if (!secret) {
        return ''
    }
    return secret
        .replace(/\s+/g, '')
        .replace(/(.{4})/g, '$1 ')
        .trim()
}

const downloadCodes = (codes: string[]) => {
    const blob = new Blob([codes.join('\n')], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'smartplayer-backup-codes.txt'
    link.click()
    URL.revokeObjectURL(url)
}

const copyText = async (text: string) => {
    try {
        await navigator.clipboard.writeText(text)
    } catch {
        return
    }
}

const printCodes = (codes: string[]) => {
    const win = window.open('', '_blank', 'noopener,noreferrer,width=480,height=640')
    if (!win) {
        return
    }
    win.document.write(`<pre style="font-family:monospace;font-size:16px;padding:24px">${codes.join('\n')}</pre>`)
    win.document.close()
    win.focus()
    win.print()
}

const useResendCountdown = (seconds?: number, active?: boolean) => {
    const [left, setLeft] = useState(0)

    useEffect(() => {
        if (!active) {
            setLeft(0)
            return
        }
        const initial = Math.max(0, seconds || 0)
        setLeft(initial)
        if (!initial) {
            return
        }
        const timer = window.setInterval(() => {
            setLeft((prev) => (prev <= 1 ? 0 : prev - 1))
        }, 1000)
        return () => window.clearInterval(timer)
    }, [seconds, active])

    return left
}

const SecondFactorActionModalComponent: FunctionComponent<SecondFactorActionModalProps> = ({
    open,
    step,
    isBusy,
    errorKey,
    begin,
    setup,
    recoveryCodes,
    proofCode,
    totpCode,
    savedAck,
    maskedEmail,
    isEmailConfirmed = false,
    pendingAction,
    onChangeProofCode,
    onChangeTotpCode,
    onChangeSavedAck,
    onSubmitProof,
    onResendProof,
    onSubmitTotpSetup,
    onFinalizeRecoveryCodes,
    onCompleteDone,
    onClose,
}) => {
    const [showManualKey, setShowManualKey] = useState(false)
    const [useBackupProof, setUseBackupProof] = useState(false)
    useEffect(() => {
        if (step !== 'totp_setup') {
            setShowManualKey(false)
        }
    }, [step])
    useEffect(() => {
        setUseBackupProof(false)
    }, [begin?.actionToken, step])
    const canToggleBackupProof = begin?.proofMethod === 'totp'
    const proofAsBackup = begin?.proofMethod === 'recovery' || useBackupProof
    const proofReady = begin
        ? proofAsBackup
            ? isBackupCodeComplete(proofCode)
            : isProofCodeReady(begin.proofMethod, proofCode, begin.codeLength)
        : false
    const totpReady = isOtpComplete(totpCode)
    const isRegenerate = pendingAction?.action === 'regenerate'
    const isUnbindTotp = pendingAction?.intent === 'unbind_totp'
    const isUnbindToEmail = isUnbindTotp && pendingAction?.method === 'email'
    const isUnbindToOff = isUnbindTotp && pendingAction?.method === 'off'
    const closable = step === 'done' || isRegenerate || step !== 'recovery_codes' || !recoveryCodes.length
    const isTotpBind =
        pendingAction?.action === 'select' && pendingAction.method === 'totp'
            ? true
            : step === 'totp_setup' || (step === 'recovery_codes' && !!setup)
    const showStepper = step !== 'done' && (isTotpBind || (step === 'proof' && pendingAction?.method === 'totp'))
    const stepperActive: SecondFactorStepperIndex = step === 'proof' ? 1 : step === 'totp_setup' ? 2 : 3
    const resendLeft = useResendCountdown(begin?.resendAfterS, step === 'proof' && begin?.proofMethod === 'email')
    const accountLabel = useMemo(() => {
        if (maskedEmail) {
            return maskedEmail
        }
        return ''
    }, [maskedEmail])
    const title = isUnbindTotp
        ? translate('totpUnbindModalTitle')
        : isRegenerate
        ? translate('totpRegenerateModalTitle')
        : isTotpBind || showStepper
        ? translate('totpConnectAppModalTitle')
        : step === 'recovery_codes'
        ? translate('totpBackupCodesTitle')
        : translate('secondFactorProofTitle')
    const proofLead = proofAsBackup
        ? translate('secondFactorProofLeadBackup')
        : begin?.proofMethod === 'email' && accountLabel
        ? translate('secondFactorProofLeadEmail').replace('{{email}}', accountLabel)
        : begin?.proofMethod === 'totp'
        ? translate('secondFactorProofLeadTotp')
        : translate('secondFactorProofLead')
    const recoveryLead = isRegenerate ? translate('totpRegenerateCodesHint') : translate('totpBackupCodesHint')

    const toggleProofMode = () => {
        setUseBackupProof((prev) => !prev)
        onChangeProofCode('')
    }

    const handleClose = () => {
        if (step === 'done') {
            onCompleteDone()
            return
        }
        if (closable && !isBusy) {
            onClose()
        }
    }

    return (
        <Modal open={open} onClose={handleClose} mod="black" showCloseIcon={false}>
            <div className={styles.overlay}>
                <div className={cn(styles.modal, step === 'totp_setup' && styles.modalWide)}>
                    <div className={styles.header}>
                        <div className={styles.title}>{title}</div>
                        {closable ? (
                            <button type="button" className={styles.closeBtn} onClick={handleClose} aria-label="close">
                                <Icon type="close" size={24} />
                            </button>
                        ) : null}
                    </div>

                    <div className={styles.body}>
                        {showStepper ? <SecondFactorActionStepper active={stepperActive} /> : null}

                        {step === 'proof' && (
                            <>
                                <div className={styles.lead}>{proofLead}</div>
                                {proofAsBackup ? (
                                    <OtpCodeInput
                                        value={proofCode}
                                        length={8}
                                        mode="alphanumeric"
                                        align="start"
                                        onChange={onChangeProofCode}
                                        onComplete={(value) => {
                                            if (!isBusy) {
                                                onSubmitProof(value)
                                            }
                                        }}
                                        autoComplete="off"
                                        autoFocus
                                        error={!!errorKey}
                                    />
                                ) : (
                                    <OtpCodeInput
                                        value={proofCode}
                                        length={begin?.codeLength || 6}
                                        align="start"
                                        onChange={onChangeProofCode}
                                        onComplete={(value) => {
                                            if (!isBusy) {
                                                onSubmitProof(value)
                                            }
                                        }}
                                        autoComplete="off"
                                        autoFocus
                                        error={!!errorKey}
                                    />
                                )}
                                {errorKey ? <div className={styles.error}>{translate(errorKey)}</div> : null}
                                {begin?.proofMethod === 'email' ? (
                                    <div className={styles.resendRow}>
                                        {resendLeft > 0 ? (
                                            <>
                                                <span className={styles.muted}>
                                                    {translate('secondFactorResendCode')}
                                                </span>
                                                <span className={styles.mutedStrong}>
                                                    {translate('secondFactorResendIn').replace(
                                                        '{{seconds}}',
                                                        String(resendLeft)
                                                    )}
                                                </span>
                                            </>
                                        ) : (
                                            <button
                                                type="button"
                                                className={styles.linkBtn}
                                                disabled={isBusy || !onResendProof}
                                                onClick={onResendProof}
                                            >
                                                {translate('secondFactorResendCode')}
                                            </button>
                                        )}
                                    </div>
                                ) : null}
                                {canToggleBackupProof ? (
                                    <button type="button" className={styles.linkBtn} onClick={toggleProofMode}>
                                        {useBackupProof
                                            ? translate('useAuthenticatorCodeInstead')
                                            : translate('useBackupCodeInstead')}
                                    </button>
                                ) : null}
                            </>
                        )}

                        {step === 'totp_setup' && setup && (
                            <div className={styles.qrLayout}>
                                <div className={styles.qrCol}>
                                    <div className={styles.qrBox}>
                                        <div className={styles.qrInner}>
                                            {setup.qrCode ? (
                                                <img src={setup.qrCode} alt="QR" width={204} height={204} />
                                            ) : setup.uri ? (
                                                <QRCode value={setup.uri} size={180} viewBox="0 0 180 180" />
                                            ) : null}
                                        </div>
                                    </div>
                                    <div className={styles.qrCaption}>
                                        <div>{translate('totpQrAppLabel')}</div>
                                        {accountLabel ? <div>({accountLabel})</div> : null}
                                    </div>
                                </div>

                                <div className={styles.howtoCol}>
                                    <div className={styles.howtoItem}>
                                        <div className={styles.howtoHead}>
                                            <div className={styles.howtoNum}>1</div>
                                            <div className={styles.howtoText}>
                                                <div className={styles.howtoTitle}>
                                                    {translate('totpHowtoInstallTitle')}
                                                </div>
                                                <div className={styles.howtoDesc}>
                                                    {translate('totpHowtoInstallDesc')}
                                                </div>
                                            </div>
                                        </div>
                                        <div className={styles.appsRow}>
                                            {TOTP_APPS.map((app) => (
                                                <span key={app} className={styles.appPill}>
                                                    {app}
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    <div className={styles.howtoItem}>
                                        <div className={styles.howtoHead}>
                                            <div className={styles.howtoNum}>2</div>
                                            <div className={styles.howtoText}>
                                                <div className={styles.howtoTitle}>
                                                    {translate('totpHowtoScanTitle')}
                                                </div>
                                                <div className={styles.howtoDesc}>{translate('totpHowtoScanDesc')}</div>
                                            </div>
                                        </div>
                                        <div className={styles.howtoExtra}>
                                            <button
                                                type="button"
                                                className={styles.linkBtn}
                                                onClick={() => setShowManualKey((v) => !v)}
                                            >
                                                <Icon
                                                    type={showManualKey ? 'view' : 'lock'}
                                                    size={16}
                                                    color="#286cf2"
                                                />
                                                {showManualKey
                                                    ? translate('totpHideManualKey')
                                                    : translate('totpShowManualKey')}
                                            </button>
                                            {showManualKey ? (
                                                <div className={styles.secretPanel}>
                                                    {accountLabel ? (
                                                        <div className={styles.secretRow}>
                                                            <span className={styles.secretKeyLabel}>
                                                                {translate('totpManualAccount')}
                                                            </span>
                                                            <span>{accountLabel}</span>
                                                        </div>
                                                    ) : null}
                                                    <div className={styles.secretRow}>
                                                        <span className={styles.secretKeyLabel}>
                                                            {translate('totpManualKey')}
                                                        </span>
                                                        <span className={styles.secretValue}>
                                                            {formatSecret(setup.secret)}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            className={styles.linkBtn}
                                                            onClick={() => setup.secret && copyText(setup.secret)}
                                                        >
                                                            <Icon type="copy" size={16} color="#286cf2" />
                                                        </button>
                                                    </div>
                                                    <div className={styles.secretRow}>
                                                        <span className={styles.secretKeyLabel}>
                                                            {translate('totpManualType')}
                                                        </span>
                                                        <span>{translate('totpManualTypeValue')}</span>
                                                    </div>
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>

                                    <div className={styles.howtoItem}>
                                        <div className={styles.howtoHead}>
                                            <div className={styles.howtoNum}>3</div>
                                            <div className={styles.howtoText}>
                                                <div className={styles.howtoTitle}>
                                                    {translate('totpHowtoConfirmTitle')}
                                                </div>
                                                <div className={styles.howtoDesc}>
                                                    {translate('totpHowtoConfirmDesc')}
                                                </div>
                                            </div>
                                        </div>
                                        <div className={styles.howtoExtra}>
                                            <OtpCodeInput
                                                value={totpCode}
                                                align="start"
                                                onChange={onChangeTotpCode}
                                                onComplete={(value) => {
                                                    if (!isBusy) {
                                                        onSubmitTotpSetup(value)
                                                    }
                                                }}
                                                autoFocus
                                                error={!!errorKey}
                                            />
                                        </div>
                                    </div>
                                </div>
                                {errorKey ? <div className={styles.error}>{translate(errorKey)}</div> : null}
                            </div>
                        )}

                        {step === 'recovery_codes' && (
                            <div className={styles.recoveryBlock}>
                                <div className={styles.recoveryLead}>{recoveryLead}</div>
                                <div className={styles.codesBox}>
                                    {recoveryCodes.map((code) => (
                                        <div key={code} className={styles.codeItem}>
                                            {code}
                                        </div>
                                    ))}
                                </div>
                                <div className={styles.actionsRow}>
                                    <Button
                                        className={styles.actionBtn}
                                        size="s"
                                        mod="default"
                                        icon={{ type: 'download', size: 16, position: 'left' }}
                                        onClick={() => downloadCodes(recoveryCodes)}
                                    >
                                        {translate('download')}
                                    </Button>
                                    <Button
                                        className={styles.actionBtn}
                                        size="s"
                                        mod="default"
                                        icon={{ type: 'copy', size: 16, position: 'left' }}
                                        onClick={() => copyText(recoveryCodes.join('\n'))}
                                    >
                                        {translate('totpCopyCodes')}
                                    </Button>
                                    <Button
                                        className={styles.actionBtn}
                                        size="s"
                                        mod="default"
                                        icon={{ type: 'file', size: 16, position: 'left' }}
                                        onClick={() => printCodes(recoveryCodes)}
                                    >
                                        {translate('print')}
                                    </Button>
                                </div>
                                <Checkbox
                                    size={18}
                                    checked={savedAck}
                                    onChange={(checked) => onChangeSavedAck(checked)}
                                    checkedIcon={{ type: 'checkbox_on_v2', color: 'blue' }}
                                    notCheckedIcon={{ type: 'checkbox_off_v2', color: 'cloud' }}
                                    checkboxWrapper={styles.checkboxRow}
                                >
                                    <span>{translate('totpBackupCodesSavedCheck')}</span>
                                </Checkbox>
                                {errorKey ? <div className={styles.error}>{translate(errorKey)}</div> : null}
                            </div>
                        )}

                        {step === 'done' && (
                            <div className={styles.doneBlock}>
                                {isUnbindTotp ? (
                                    <>
                                        <div className={styles.doneTitle}>{translate('totpUnbindDoneTitle')}</div>
                                        <div className={styles.doneLead}>
                                            {isUnbindToOff
                                                ? translate('totpUnbindDoneLeadOff')
                                                : translate('totpUnbindDoneLeadEmail').replace(
                                                      '{{email}}',
                                                      accountLabel || translate('email')
                                                  )}
                                        </div>
                                        {isEmailConfirmed && isUnbindToEmail && accountLabel ? (
                                            <div className={styles.doneNotify}>
                                                {translate('totpUnbindDoneNotify').replace('{{email}}', accountLabel)}
                                            </div>
                                        ) : null}
                                    </>
                                ) : isRegenerate ? (
                                    <>
                                        <div className={styles.doneTitle}>{translate('totpRegenerateDoneTitle')}</div>
                                        {isEmailConfirmed && accountLabel ? (
                                            <div className={styles.doneLead}>
                                                {translate('totpRegenerateDoneLead').replace('{{email}}', accountLabel)}
                                            </div>
                                        ) : null}
                                    </>
                                ) : (
                                    <>
                                        <div className={styles.doneTitle}>{translate('totpConnectDoneTitle')}</div>
                                        <div className={styles.doneLead}>{translate('totpConnectDoneLead')}</div>
                                        {isEmailConfirmed && accountLabel ? (
                                            <div className={styles.doneNotify}>
                                                {translate('totpConnectDoneNotify').replace('{{email}}', accountLabel)}
                                            </div>
                                        ) : null}
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    <div className={styles.footer}>
                        {step === 'done' ? (
                            <Button size="m" mod="fillBlack" onClick={onCompleteDone}>
                                {translate(isRegenerate || isUnbindTotp ? 'close' : 'totpConnectDoneFinish')}
                            </Button>
                        ) : (
                            <>
                                {step !== 'recovery_codes' || isRegenerate ? (
                                    <Button size="m" mod="default" disabled={isBusy} onClick={handleClose}>
                                        {translate('cancel')}
                                    </Button>
                                ) : null}
                                {step === 'proof' ? (
                                    <Button
                                        size="m"
                                        mod="fillBlack"
                                        disabled={isBusy || !proofReady}
                                        onClick={onSubmitProof}
                                    >
                                        {translate('continue')}
                                    </Button>
                                ) : null}
                                {step === 'totp_setup' ? (
                                    <Button
                                        size="m"
                                        mod="fillBlack"
                                        disabled={isBusy || !totpReady}
                                        onClick={onSubmitTotpSetup}
                                    >
                                        {translate('continue')}
                                    </Button>
                                ) : null}
                                {step === 'recovery_codes' ? (
                                    <Button
                                        size="m"
                                        mod="fillBlack"
                                        disabled={isBusy || !savedAck}
                                        onClick={onFinalizeRecoveryCodes}
                                    >
                                        {translate('continue')}
                                    </Button>
                                ) : null}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    )
}

export const SecondFactorActionModal = memo(SecondFactorActionModalComponent)
